import React from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { PATH_MAP } from "../../configuration/PathMap"
import Animate, { AnimationTypes } from "../Animate/Animate"
import { useSignUpContext } from "../../context/useSignUpContext"
import { SignUp } from "../../Objects/SignUp/SignUp"
import { trackMarketingEvent } from "../../analytics/tracking"
import { useGetLandingVideos } from "../../hooks/Landing/useGetLandingVideos"
import { extractYoutubeId } from "../../Objects/Generics/Helps"
import { resolveAudienceImagePath } from "../../pages/LandPage/audienceImages"
import LandingYoutubeFrame from "./LandingYoutubeFrame"

const heroCarouselIntervalMs = 3000

type AudienceImageContent = {
  badge: string
  heroImagePath: string
}

type HeroSlide = {
  alt: string
  src: string
}

const Hero: React.FC = () => {
  const { t } = useTranslation("login")

  const { t: tSeoLanding } = useTranslation("seoLanding")

  const { updateSignUp } = useSignUpContext()

  const navigate = useNavigate()

  const [isHeroImageLoading, setIsHeroImageLoading] = React.useState(true)

  const [visibleHeroSlide, setVisibleHeroSlide] = React.useState<HeroSlide | null>(null)

  const [heroYoutubeUrl, setHeroYoutubeUrl] = React.useState<string | null>(null)

  const [heroSlideIndex, setHeroSlideIndex] = React.useState(0)

  const { handleGetLandingVideos } = useGetLandingVideos()

  const heroVideoId = extractYoutubeId(heroYoutubeUrl ?? "")

  const audienceItems = tSeoLanding("audiences.items", { returnObjects: true }) as AudienceImageContent[]

  const heroSlides = React.useMemo(
    () =>
      audienceItems
        .map((item) => ({
          alt: item.badge,
          src: resolveAudienceImagePath(item.heroImagePath),
        }))
        .filter((item) => item.src),
    [audienceItems],
  )

  const currentHeroSlide = heroSlides[heroSlideIndex % Math.max(heroSlides.length, 1)] as HeroSlide | undefined

  const isTransitioningHeroSlide = Boolean(
    currentHeroSlide && visibleHeroSlide && visibleHeroSlide.src !== currentHeroSlide.src,
  )

  React.useEffect(() => {
    handleGetLandingVideos()
      .then((value) => setHeroYoutubeUrl(value.homeYoutubeUrl))
      .catch(() => setHeroYoutubeUrl(null))
  }, [handleGetLandingVideos])

  React.useEffect(() => {
    if (heroVideoId || heroSlides.length <= 1) return

    const intervalId = window.setInterval(() => {
      setIsHeroImageLoading(true)
      setHeroSlideIndex((current) => (current + 1) % heroSlides.length)
    }, heroCarouselIntervalMs)

    return () => window.clearInterval(intervalId)
  }, [heroSlides.length, heroVideoId])

  const handleHeroImageLoad = () => {
    if (!currentHeroSlide) return

    setVisibleHeroSlide(currentHeroSlide)
    setIsHeroImageLoading(false)
  }

  const handleCreateRestaurantAccount = () => {
    trackMarketingEvent("landing_cta_click", {
      source: "hero",
      cta: "restaurant_signup_first_step",
    })
    updateSignUp(new SignUp())
    navigate(PATH_MAP.ROOT_SIGN_UP)
  }

  const handleRestaurantLogin = () => {
    trackMarketingEvent("landing_cta_click", {
      source: "hero",
      cta: "restaurant_login",
    })
    navigate(PATH_MAP.ROOT_LOGIN)
  }

  const handleCourierAction = (mode: "signup" | "signin") => {
    trackMarketingEvent("landing_select_profile", {
      source: "hero_secondary",
      profile: "courier",
      action: mode,
    })
    const query = mode === "signup" ? "?mode=signup" : "?mode=signin"

    updateSignUp(new SignUp())
    navigate(`${PATH_MAP.COURIER_PANEL_PAGE}${query}`)
  }

  return (
    <>
      <Animate type={AnimationTypes.fadeIn}>
        <section className="relative overflow-hidden pt-4 lg:pb-12">
          <div className="max-w-[1280px] mx-auto px-6">
            <div className="grid gap-8 text-center lg:grid-cols-2 lg:text-left lg:items-center">
              <div className="order-1">
                <Animate type={AnimationTypes.fadeInTraslate} duration="1.8s">
                  <h1 className="text-4xl font-black leading-[1.1] text-slate-900 mb-4">
                    {t("label_hero_title1")}{" "}
                    <span className="bg-gradient-to-r from-red-600 to-orange-500 bg-clip-text text-transparent">
                      {t("label_hero_title2")}
                    </span>
                  </h1>
                </Animate>

                {/*<Animate type={AnimationTypes.fadeInTraslate} duration="2s">
                  <p className="text-base lg:text-xl text-slate-600 max-w-[560px] text-justify mx-auto lg:mx-0 mb-8 leading-relaxed">
                    {t("label_hero_description")}
                  </p>
                </Animate>*/}

                <div className="flex flex-col items-center md:items-start sm:flex-row gap-3 sm:flex-wrap">
                  <Animate type={AnimationTypes.slideRightIn} duration="2.5s">
                    <button
                      onClick={handleCreateRestaurantAccount}
                      className="hidden cursor-pointer bg-gradient-to-r from-red-600 to-orange-500 text-white px-8 py-4 rounded-2xl font-bold text-lg transition-all duration-300 shadow-lg shadow-red-500/30 hover:scale-105 active:scale-95 md:inline-flex"
                      data-interaction-description-key="interactionDescriptions:components.landPage.hero.button.handleCreateRestaurantAccount"
                    >
                      {t("label_hero_my_account")}
                    </button>
                  </Animate>

                  <Animate type={AnimationTypes.slideRightIn} duration="2.8s">
                    <button
                      onClick={handleRestaurantLogin}
                      className="cursor-pointer bg-gradient-to-r from-red-500 to-orange-400 text-white px-4 md:px-8 md:py-4 rounded-2xl font-bold text-lg transition-all duration-300 shadow-lg shadow-red-500/30 hover:scale-105 active:scale-95 md:inline-flex"
                      data-interaction-description-key="interactionDescriptions:components.landPage.hero.button.handleRestaurantLogin"
                    >
                      {t("label_hero_enter")}
                    </button>
                  </Animate>
                </div>

                <Animate type={AnimationTypes.fadeInTraslate} duration="3.4s">
                  <div className="mt-4 flex flex-wrap items-center justify-center gap-x-3 gap-y-2 text-sm text-slate-500 lg:justify-start">
                    <span className="font-semibold">{t("label_hero_courier_secondary_title")}</span>

                    <button
                      type="button"
                      onClick={() => handleCourierAction("signup")}
                      className="cursor-pointer font-bold text-slate-700 underline-offset-4 hover:text-orange-600 hover:underline"
                      data-interaction-description-key="interactionDescriptions:components.landPage.hero.button.handleCourierActionSignup"
                    >
                      {t("label_hero_courier_secondary_create")}
                    </button>

                    <span aria-hidden>|</span>

                    <button
                      type="button"
                      onClick={() => handleCourierAction("signin")}
                      className="cursor-pointer font-bold text-slate-700 underline-offset-4 hover:text-orange-600 hover:underline"
                      data-interaction-description-key="interactionDescriptions:components.landPage.hero.button.handleCourierActionSignin"
                    >
                      {t("label_hero_courier_secondary_login")}
                    </button>
                  </div>
                </Animate>
              </div>

              <div className="hidden lg:order-2 lg:block">
                <div className="relative order-1 mt-8 flex justify-center lg:mt-0">
                  <div
                    className={
                      heroVideoId
                        ? "w-full max-w-[560px]"
                        : "w-full max-w-[560px] overflow-hidden rounded-2xl  bg-slate-50 p-2 shadow-sm"
                    }
                  >
                    <Animate type={AnimationTypes.fadeInTraslate} duration="2.8s">
                      {heroVideoId ? (
                        <LandingYoutubeFrame
                          youtubeUrl={heroYoutubeUrl}
                          title={t("label_hero_video_title")}
                          aspectRatio="16 / 10"
                          frameWrapperClassName="w-full"
                        />
                      ) : currentHeroSlide ? (
                        <div className="relative aspect-[16/10] w-full overflow-hidden rounded-lg bg-slate-100">
                          {isHeroImageLoading && (
                            <div className="pointer-events-none absolute inset-0 z-10">
                              {!visibleHeroSlide && (
                                <div className="absolute inset-0 bg-gradient-to-r from-orange-50 via-white to-amber-50" />
                              )}

                              <div className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-orange-300/55 to-transparent animate-[shimmer_1.05s_infinite]" />
                            </div>
                          )}

                          {isTransitioningHeroSlide && visibleHeroSlide && (
                            <img
                              src={visibleHeroSlide.src}
                              alt={visibleHeroSlide.alt}
                              className="absolute inset-0 h-full w-full object-contain opacity-85 blur-[0.4px] transition-all duration-700 ease-out"
                              aria-hidden="true"
                            />
                          )}

                          <img
                            key={currentHeroSlide.src}
                            src={currentHeroSlide.src}
                            alt={currentHeroSlide.alt}
                            className={`absolute inset-0 h-full w-full object-contain transition-all duration-700 ease-out ${
                              isTransitioningHeroSlide && isHeroImageLoading
                                ? "translate-x-5 scale-[0.98] opacity-0"
                                : "translate-x-0 scale-100 opacity-100"
                            }`}
                            onLoad={handleHeroImageLoad}
                            onError={() => setIsHeroImageLoading(false)}
                          />

                          <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-white/20 via-transparent to-slate-900/10" />

                          <div className="absolute inset-x-4 bottom-3 flex gap-1.5">
                            {heroSlides.slice(0, 8).map((slide) => (
                              <span
                                key={slide.src}
                                className={`h-1.5 flex-1 rounded-full transition-colors duration-500 ${
                                  slide.src === currentHeroSlide.src ? "bg-orange-500" : "bg-slate-300"
                                }`}
                              />
                            ))}
                          </div>
                        </div>
                      ) : null}
                    </Animate>
                  </div>

                  <div className="absolute -bottom-4 right-4 hidden bg-white px-5 py-3 rounded-xl shadow-lg border border-slate-100 sm:flex items-center gap-3">
                    <div className="w-2 h-2 bg-emerald-500 rounded-full shadow-[0_0_0_4px_rgba(16,185,129,0.2)]" />
                    <p className="text-[0.7rem] font-extrabold uppercase text-slate-900">
                      {t("label_hero_operational")}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </Animate>
    </>
  )
}

export default Hero
